import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import ModalBody from '../../Modal/ModalBody';
import SfhPrimaryButton from './SfhPrimaryButton';
import SectionLoader from '../../Loader/SectionLoader';
import RowItemElement from '../../Buy/RowItem';
import SelectPaymentOptions from '../../Buy/SelectPaymentOptions';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import ProductImage from '../../LegacyOrder/ProductImage';
import PriceLockTimer from '../../Common/Timers/PriceLockTimer';
import Paragraph from '../../Paragraph/Paragraph';
import AnchorOnClick from '../../Anchor/AnchorOnClick';
import ErrorDrawer from '../../Buy/ErrorDrawer';
import { ReactComponent as RowInfoIcon } from '../../../assets/images/svg/sfh-info-secondary.svg';
import TermsAndConditions from '../MetalStream/TermsAndConditions';
import { ReactComponent as Checked } from '../../../assets/images/svg/checked.svg';
import { ReactComponent as Unchecked } from '../../../assets/images/svg/unchecked.svg';
import {
  findDefaultPaymentMethod,
  formatPaymentMethodLabel,
  mapCreditCardsToPaymentMethods,
} from './sellFromHomePaymentHelpers';
import { resolveSfhVaultSymbol } from './sellFromHomeVaultHelpers';
import { ReactComponent as SfhCreditCard } from '../../../assets/images/svg/sfh-credit-card.svg';
import {
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../../util/enum/api/paymentTypes';
import { postPaidApiErrors } from '../../../util/enum/api/postPaidApiErrors';
import { fetchCreditCards } from '../../../store/actions/creditCard/creditCardActions';
import { fetchProductSymbolPrices } from '../../../store/actions/products/productActions';
import {
  getQuotesForOrderSellFromHome,
  executeQuotesForOrderSellFromHome,
} from '../../../store/actions/orders/orderActions';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAddresses,
  selectDefaultMailingAddress,
} from '../../../store/selectors/accountSelectors';
import { selectCreditCards } from '../../../store/selectors/creditCardSelectors';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import { PRODUCT_SYMBOL_PRICES_LOADING } from '../../../store/actions/products/productActionConstants';
import {
  ADD_ADDRESS_WIZARD,
  SIGNIFYD_SESSION_ID,
} from '../../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../../util/helpers/sessionStorageHelper';
import loadSignifydDeviceFingerprintScript, {
  loadSignifydDeviceFingerprintScriptWithoutSessionId,
} from '../../../signifydDeviceFingerprint';
import { generateSessionOrderId } from '../../../util/helpers/sinigfyd';
import { countryIso3ToIso2 } from '../../../util/helpers/addressHelper';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import {
  accountTypeKeys,
  accountTypes,
} from '../../../util/enum/api/accountTypes';
import useErrorDrawer from '../../../util/hooks/useErrorDrawer';
import SellFromHomeBankTitleNoticeDrawer from './SellFromHomeBankTitleNoticeDrawer';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_REQUEST_SUBMITTED,
  SFH_TC_AGREED,
  SFH_CHANGE_CC_CLICKED,
  SFH_ADD_CC_CLICKED,
} from '../../../constants/gtmEvents';

const PRE_AUTH_RATE = 0.05;
const DEFAULT_LOCK_SECONDS = 30;
const NOT_AVAILABLE = 'N/A';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  opacity: 0.4;
  width: 100%;
  margin: 0;

  ${({ $bleed }) =>
    $bleed &&
    `
      width: auto;
      margin-left: -16px;
      margin-right: -16px;
    `}
`;

const Footer = styled.div`
  > button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: ${themeColors.colorGrey};
  }

  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const ScrollRegion = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-bottom: 5px;
`;

const BottomSection = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 1.125rem;
`;

const ProductImageWrap = styled.div`
  flex-shrink: 0;

  > img {
    width: 72px;
    height: 72px;
    object-fit: contain;
    margin: 0;
  }
`;

const ProductTextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const SellingLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
`;

const ProductCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const DropOffLine = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  padding: 14px 0;

  > b {
    font-weight: 600;
  }
`;

const TimerWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const PayoutRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
`;

const PayoutLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PayoutLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
`;

const PayoutSubLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const PayoutAmount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorPayoutSuccess};
  white-space: nowrap;
`;

const PreAuthCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 10px;
`;

const PreAuthRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
`;

const PreAuthLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const PreAuthLeftWithIcon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const PreAuthLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
`;

const PreAuthSubLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;
const PreAuthSubLabelWithIcon = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1;
  color: ${themeColors.colorTextSecondary};
  display: inline-flex;
  align-items: center;
  gap: 4px;

  > svg {
    display: block;
    flex-shrink: 0;
  }
`;
const PreAuthAmount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
`;

const PaymentRow = styled.div`
  > div {
    border-top: none;
  }
`;

// Non-scrolling T&C acceptance row, pinned in the bottom section above the
// payment method. Clicking the row toggles the box; the "Terms & Conditions"
// link opens the T&C modal without toggling (link click is isolated).
const TermsRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 12px;
  cursor: pointer;
`;

const TermsCheckboxInput = styled.input`
  display: none;
`;

const TermsIndicator = styled.span`
  flex-shrink: 0;
  display: inline-flex;

  svg {
    width: 20px;
    height: 20px;
    color: ${themeColors.colorSfhCheckboxBorder};
  }
`;

const TermsText = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const TermsLink = styled.a`
  color: ${themeColors.colorLinkPrimary};
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
`;

const TermsPdfPages = styled.div`
  width: 100%;

  canvas {
    max-width: 100%;
  }

  .react-pdf__Page {
    margin-bottom: 12px;
  }
`;

const SellFromHomeReview = ({
  selectedHit,
  quantity,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedOriginAddress,
  initialQuote,
  termsAccepted,
  setTermsAccepted,
  termsPdfUrl,
  isTermsContentLoading,
  handleSubmitOrder,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sfhGtmEvent } = useSfhGtmHook();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const productBySymbol = useSelector(getProductBySymbol);
  const creditCards = useSelector(selectCreditCards);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const settings = useSelector(selectSettings);
  const isLoadingPrice = useSelector(
    selectIsLoadingByActionTypes([PRODUCT_SYMBOL_PRICES_LOADING]),
  );

  const lockSeconds = Math.max(
    1,
    Math.round(
      (settings?.PricingRefreshFrequencyMilliseconds ??
        DEFAULT_LOCK_SECONDS * 1000) / 1000,
    ),
  );

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isBankTitleNoticeOpen, setIsBankTitleNoticeOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [termsNumPages, setTermsNumPages] = useState(0);
  const [termsPdfWidth, setTermsPdfWidth] = useState(0);
  const termsPdfContainerRef = useRef(null);

  const [quote, setQuote] = useState(initialQuote || null);
  const skipInitialFetchRef = useRef(Boolean(initialQuote));
  const [counter, setCounter] = useState(lockSeconds + 2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);
  const quoteRequestIdRef = useRef(0);
  const {
    isOpen: isErrorOpen,
    errorTitle,
    errorMessage,
    showError,
    hideError,
  } = useErrorDrawer();

  const openSupport = useCallback(() => setIsSupportOpen(true), []);
  const closeSupport = useCallback(() => setIsSupportOpen(false), []);

  const accountType = parseEnumType(accountTypeKeys, account?.AccountType);
  const isCorporateOrTrust =
    accountType === accountTypes.Corporate ||
    accountType === accountTypes.Trust;
  const bankTitleNoticeShownRef = useRef(false);

  // Corporate/Trust users must be shown the bank-account-title notice the first
  // time they reach the Review page. It auto-opens once (guarded by the ref) and
  // won't reopen after the user dismisses it via Understood or the close icon.
  useEffect(() => {
    if (isCorporateOrTrust && !bankTitleNoticeShownRef.current) {
      bankTitleNoticeShownRef.current = true;
      setIsBankTitleNoticeOpen(true);
    }
  }, [isCorporateOrTrust]);

  const openTerms = useCallback((event) => {
    // Keep the link click from toggling the wrapping checkbox label — opening
    // the T&C should never change the acceptance state on its own.
    event.preventDefault();
    event.stopPropagation();
    setIsTermsOpen(true);
  }, []);
  const closeTerms = useCallback(() => setIsTermsOpen(false), []);
  const acceptTermsFromModal = useCallback(() => {
    setTermsAccepted(true);
    setIsTermsOpen(false);
  }, [setTermsAccepted]);

  const braintreeErrorMessage = useCallback(
    (apiMessage) => (
      <div>
        <Paragraph fullWidth marginBottom={12}>
          {apiMessage}
        </Paragraph>
        <Paragraph marginBottom={24}>
          {t('buyWizard.postPaid.paymentFailureContactSupport1')}
          <AnchorOnClick
            onClick={(event) => {
              event.preventDefault();
              openSupport();
            }}
          >
            {t('buyWizard.postPaid.paymentFailureContactSupport2')}
          </AnchorOnClick>
          {t('buyWizard.postPaid.paymentFailureContactSupport3')}
        </Paragraph>
      </div>
    ),
    [openSupport, t],
  );

  const productCode = selectedHit?.Code;
  // The /quotes endpoint validates against the per-vault broker symbol, not
  // the catalog code — same resolution we use on the Quantity step.
  const vaultSymbol = useMemo(
    () => resolveSfhVaultSymbol(selectedHit, account),
    [selectedHit, account],
  );

  useEffect(() => {
    if (accountUid && productCode) {
      dispatch(
        fetchProductSymbolPrices({
          accountUid,
          symbol: productCode,
          side: 'sell',
        }),
      );
    }
  }, [accountUid, productCode, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchCreditCards({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  useEffect(() => {
    if (!isTermsOpen) return undefined;
    const measure = () => {
      if (termsPdfContainerRef.current) {
        setTermsPdfWidth(termsPdfContainerRef.current.clientWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isTermsOpen]);

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!sessionId) {
      if (existingScript) {
        existingScript.dataset.orderSessionId = generateSessionOrderId();
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          existingScript.dataset.orderSessionId,
        );
        return;
      }
      loadSignifydDeviceFingerprintScript();
      const script = document.getElementById('sig-api');
      if (script) {
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          script.dataset.orderSessionId,
        );
      }
    } else if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const hitPrice =
    selectedHit?.ProductPrice ??
    selectedHit?.SellPrice ??
    selectedHit?.BidPrice ??
    selectedHit?.PricePerUnit ??
    selectedHit?.SellPricePerUnit;
  const apiPrice = productBySymbol?.ProductPrice;
  const unitPrice = Number(hitPrice ?? apiPrice ?? 0) || 0;

  const creditCardPaymentMethods = useMemo(
    () => mapCreditCardsToPaymentMethods(creditCards),
    [creditCards],
  );

  const defaultPaymentMethod = useMemo(
    () => findDefaultPaymentMethod(creditCardPaymentMethods),
    [creditCardPaymentMethods],
  );

  useEffect(() => {
    if (!selectedPaymentMethod && defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod, setSelectedPaymentMethod]);

  const activePaymentMethod = selectedPaymentMethod || defaultPaymentMethod;
  const isSegregated = Boolean(productBySymbol?.IsSegregated);

  // SFH ships FROM the user's selected verified origin address (picked on the
  // Shipping step). Cabrella's GetAvailableRates needs that origin to price
  // shipping/insurance. Until the Shipping step propagates the selection, fall
  // back to the default mailing address (then first verified, then first).
  const originAddress = useMemo(
    () =>
      selectedOriginAddress ||
      defaultMailingAddress ||
      accountAddresses?.find((a) => a?.IsVerified) ||
      accountAddresses?.[0],
    [selectedOriginAddress, defaultMailingAddress, accountAddresses],
  );
  const addressUid = originAddress?.AddressUid;

  const fetchQuote = useCallback(() => {
    if (!accountUid || !vaultSymbol || !quantity || !addressUid) return;
    quoteRequestIdRef.current += 1;
    const requestId = quoteRequestIdRef.current;
    // Only the latest request may clear the flag, so an older response landing
    // late doesn't unlock the CTA while a newer fetch is still running.
    const settle = () => {
      if (quoteRequestIdRef.current === requestId) {
        setIsRefreshingQuote(false);
      }
    };
    setIsRefreshingQuote(true);
    dispatch(
      getQuotesForOrderSellFromHome({
        getQuotesPayload: {
          accountUid,
          symbolCode: vaultSymbol,
          quantityOrAmount: quantity,
          sideType: 'Sell',
          unitType: 'Quantity',
          paymentType: activePaymentMethod?.PaymentMethodType,
          paymentTypeUid: activePaymentMethod?.PaymentMethodUid,
          addressUid,
          isSegregated,
          addPaymentMethodData: false,
          orderEntryType: 'OrderEntry',
          isSellFromHome: true,
          // dropOff = origin (where the user ships from); destination is
          // derived server-side from the vault symbol.
          dropOffCountry: countryIso3ToIso2(originAddress?.CountryIso3Code),
          dropOffState: originAddress?.StateRegion,
          dropOffCity: originAddress?.City,
          dropOffZip: originAddress?.PostalCode,
          dropOffAddress1: originAddress?.AddressLine,
          dropOffAddress2: originAddress?.AddressLine2,
        },
        onSuccess: (data) => {
          setQuote(data);
          settle();
        },
        onError: settle,
      }),
    );
  }, [
    accountUid,
    vaultSymbol,
    quantity,
    activePaymentMethod,
    addressUid,
    originAddress,
    isSegregated,
    dispatch,
  ]);

  useEffect(() => {
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    fetchQuote();
  }, [fetchQuote]);

  // Reset the visible counter whenever the underlying quote query identity
  // changes (e.g. payment changes — we'll refetch on a fresh window).
  useEffect(() => {
    setCounter(lockSeconds + 2);
  }, [fetchQuote, lockSeconds]);

  // 1s ticker drives the visual countdown and triggers refetch when it
  // bottoms out. Display value is `counter - 2` (see PriceLockTimer), so the
  // user sees lockSeconds → 0. Paused while a submit is in-flight so the
  // price the user agreed to doesn't shift under them mid-call.
  useEffect(() => {
    if (isSubmitting) return undefined;
    const id = setInterval(() => {
      setCounter((c) => {
        if (c <= 2) {
          fetchQuote();
          return lockSeconds + 2;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [fetchQuote, lockSeconds, isSubmitting]);

  const qsi = quote?.QuoteStatusItem;
  const toFee = (value) => {
    if (value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const shippingFee = toFee(qsi?.ShippingFee);
  const insuranceFee = toFee(qsi?.ShippingInsuranceFee);
  const totalChargedToday = shippingFee;
  const formatFee = (value) => {
    if (!quote) return t('sellWizard.sfhWizard.review.loading');
    return value == null ? NOT_AVAILABLE : formatMoneyNumeral(value);
  };
  const quotePayout = Number(
    qsi?.ClientNetPay ?? qsi?.TotalClientExecutionPrice,
  );
  const payout =
    Number.isFinite(quotePayout) && quotePayout > 0
      ? quotePayout
      : unitPrice * quantity;
  const preAuthAmount = payout * PRE_AUTH_RATE;

  // "Price Guaranteed /unit" on the confirmation must reflect the executed quote
  // (ClientPricePerUnit), not the static product/search price, so it stays
  // consistent with the quote-driven payout (PriceGuaranteed × Qty === Payout).
  const getGuaranteedUnitPrice = () => {
    const quoteUnitPrice = Number(qsi?.ClientPricePerUnit);
    if (Number.isFinite(quoteUnitPrice) && quoteUnitPrice > 0) {
      return quoteUnitPrice;
    }
    if (quantity > 0) {
      return payout / quantity;
    }
    return unitPrice;
  };
  const guaranteedUnitPrice = getGuaranteedUnitPrice();

  const gtmEventProps = {
    sku: vaultSymbol,
    quantity,
    unit_price: unitPrice,
    total_payout: payout,
    pre_auth_amount: preAuthAmount,
    shipping_fee: shippingFee,
    insurance_fee: insuranceFee,
    total_charged_today: totalChargedToday,
    address_selected: originAddress?.AddressUid,
  };

  const handleTermsToggle = () => {
    setTermsAccepted((prev) => {
      const next = !prev;
      if (next) sfhGtmEvent(SFH_TC_AGREED, gtmEventProps);
      return next;
    });
  };

  const handleSubmit = () => {
    if (typeof handleSubmitOrder !== 'function') return;
    const quoteUid = quote?.QuoteUid;
    if (!quoteUid) {
      showError({
        title: t('apiErrors.IssueWithYourOrder'),
        message: t('sellWizard.sfhWizard.review.quoteUnavailable'),
      });
      return;
    }

    sfhGtmEvent(SFH_REQUEST_SUBMITTED, gtmEventProps);

    hideError();
    setIsSubmitting(true);
    dispatch(
      executeQuotesForOrderSellFromHome({
        executeQuotePayload: {
          quoteUid,
          signifydSessionId:
            retrieveFromSessionStorage(SIGNIFYD_SESSION_ID) || 'null',
        },
        isLoading: setIsSubmitting,
        onSuccess: (data) => {
          handleSubmitOrder({
            unitPrice: guaranteedUnitPrice,
            payout,
            preAuthAmount,
            shippingFee,
            insuranceFee,
            addressUid: originAddress?.AddressUid,
            paymentMethod: activePaymentMethod,
            submittedAt: new Date().toISOString(),
            quoteUid,
            expiresOnUtc: quote?.ExpiresOnUtc,
            transactionUid: data?.TransactionUid,
            deliveryTransactionUid: data?.DeliveryTransactionUid,
            referenceNumber: data?.TransactionUid,
          });
        },
        onError: (error) => {
          const errorCode = error?.response?.data?.Errors?.[0]?.Code;
          const apiMessage = error?.response?.data?.Errors?.[0]?.Message;

          // Match buy wizard: Braintree declines show API Message in a
          // bottom ErrorDrawer (with contact-support link).
          if (
            errorCode === 'BraintreeTransactionError' ||
            errorCode === 'BraintreeAuthorizationError' ||
            errorCode === 'BraintreeCaptureError'
          ) {
            showError({
              title: t('buyWizard.postPaid.paymentFailureTitle'),
              message: braintreeErrorMessage(
                apiMessage || t('sellWizard.sfhWizard.review.submitFailed'),
              ),
            });
            return;
          }

          if (postPaidApiErrors.includes(errorCode)) {
            showError({
              title: t('buyWizard.postPaid.paymentFailureTitle'),
              message:
                apiMessage ||
                (errorCode
                  ? t(`apiErrors.${errorCode}`)
                  : t('sellWizard.sfhWizard.review.submitFailed')),
            });
            return;
          }

          showError({
            title: t('apiErrors.IssueWithYourOrder'),
            message: errorCode
              ? t(`apiErrors.${errorCode}`)
              : t('sellWizard.sfhWizard.review.submitFailed'),
          });
        },
      }),
    );
  };

  return (
    <SectionLoader
      isLoading={isLoadingPrice || isSubmitting || isRefreshingQuote}
    >
      <ModalBody>
        <ScrollRegion>
          <ProductRow>
            <ProductImageWrap>
              <ProductImage productCode={productCode} width={72} height={72} />
            </ProductImageWrap>
            <ProductTextCol>
              <SellingLabel>
                {t('sellWizard.sfhWizard.review.selling')}
              </SellingLabel>
              <ProductCaption>
                {t('sellWizard.sfhWizard.review.productLine', {
                  count: quantity,
                  caption: `${selectedHit?.WeightCaption ?? ''} ${
                    selectedHit?.Caption ?? ''
                  }`.trim(),
                })}
              </ProductCaption>
            </ProductTextCol>
          </ProductRow>

          <Divider />

          <DropOffLine>
            {t('sellWizard.sfhWizard.review.dropOff')
              .split('FedEx Ship Center')
              .map((part, idx, arr) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>
                  {part}
                  {idx < arr.length - 1 && <b>FedEx Ship Center</b>}
                </React.Fragment>
              ))}
          </DropOffLine>

          <Divider />

          <TimerWrap>
            <PriceLockTimer
              counter={counter}
              label={t('sellWizard.sfhWizard.review.priceLocked', {
                seconds: lockSeconds,
              })}
              labelColor={themeColors.colorTextSecondary}
            />
          </TimerWrap>
          <PreAuthCard>
            <PayoutRow>
              <PayoutLeft>
                <PayoutLabel>
                  {t('sellWizard.sfhWizard.review.payoutReceivable')}
                </PayoutLabel>
                <PayoutSubLabel>
                  {t('sellWizard.sfhWizard.review.afterVerification')}
                </PayoutSubLabel>
              </PayoutLeft>
              <PayoutAmount data-cy="text-sfh-review-payout">
                {formatMoneyNumeral(payout)}
              </PayoutAmount>
            </PayoutRow>

            <Divider />

            <PreAuthRow>
              <PreAuthLeft>
                <PreAuthLabel>
                  {t('sellWizard.sfhWizard.review.preAuthAmount')}
                </PreAuthLabel>
                <PreAuthSubLabel>
                  {t('sellWizard.sfhWizard.review.preAuthHold')}
                </PreAuthSubLabel>
              </PreAuthLeft>
              <PreAuthAmount data-cy="text-sfh-review-preauth">
                {formatMoneyNumeral(preAuthAmount)}
              </PreAuthAmount>
            </PreAuthRow>
          </PreAuthCard>

          <PreAuthCard>
            <PreAuthRow>
              <PreAuthLeftWithIcon>
                <PreAuthLabel>
                  {t('sellWizard.sfhWizard.review.shippingFee')}
                </PreAuthLabel>
                <PreAuthSubLabelWithIcon>
                  <RowInfoIcon width={15} height={15} />
                  {t('sellWizard.sfhWizard.review.insuranceFeeIncluded')}
                </PreAuthSubLabelWithIcon>
                <PreAuthSubLabel>
                  {t('sellWizard.sfhWizard.review.chargedTodayNonRefundable')}
                </PreAuthSubLabel>
              </PreAuthLeftWithIcon>
              <PreAuthAmount data-cy="text-sfh-review-shipping">
                {formatFee(totalChargedToday)}
              </PreAuthAmount>
            </PreAuthRow>
          </PreAuthCard>
        </ScrollRegion>

        <BottomSection>
          <TermsRow>
            <TermsCheckboxInput
              type="checkbox"
              checked={termsAccepted}
              onChange={handleTermsToggle}
              data-cy="checkbox-sfh-terms"
            />
            <TermsIndicator>
              {termsAccepted ? <Checked /> : <Unchecked />}
            </TermsIndicator>
            <TermsText>
              {t('sellWizard.sfhWizard.review.agreePrefix')}{' '}
              <TermsLink onClick={openTerms} data-cy="link-sfh-terms">
                {t('sellWizard.sfhWizard.review.termsLink')}
              </TermsLink>
            </TermsText>
          </TermsRow>

          {activePaymentMethod && (
            <PaymentRow>
              <RowItemElement
                label={t('sellWizard.sfhWizard.review.payWith')}
                value={
                  <>
                    {activePaymentMethod.PaymentMethodType ===
                    PAYMENT_METHOD_TYPES.CREDIT_CARD ? (
                      <SfhCreditCard />
                    ) : (
                      PAYMENT_METHOD_TYPES_ICONS[
                        activePaymentMethod.PaymentMethodType
                      ]
                    )}
                    {formatPaymentMethodLabel(activePaymentMethod, t)}
                  </>
                }
                clickLabel={t('sellWizard.sfhWizard.quantity.change')}
                onClick={() => {
                  sfhGtmEvent(SFH_CHANGE_CC_CLICKED, {
                    sku: vaultSymbol,
                    page: 'review',
                  });
                  setIsPaymentDrawerOpen(true);
                }}
              />
            </PaymentRow>
          )}

          <Footer>
            <SfhPrimaryButton
              // Disabled while a submit is in flight (avoid duplicate orders),
              // while the quote is still loading — until it resolves the shipping
              // and insurance fees show loading text, so the order total isn't final —
              // while the T&C content is still being fetched, and until the user
              // accepts the T&C (required for every order), and while the quote
              // is being refreshed (30s timer) so the price can't shift under it.
              disabled={
                isSubmitting ||
                isRefreshingQuote ||
                !quote ||
                !termsAccepted ||
                isTermsContentLoading
              }
              loading={isSubmitting}
              onClick={handleSubmit}
              data-cy="button-sfh-preauth-submit"
            >
              {t('sellWizard.sfhWizard.review.submit')}
            </SfhPrimaryButton>
          </Footer>
        </BottomSection>
      </ModalBody>

      <SellFromHomeBankTitleNoticeDrawer
        isOpen={isBankTitleNoticeOpen}
        onClose={() => setIsBankTitleNoticeOpen(false)}
      />

      <ErrorDrawer
        open={isErrorOpen}
        errorTitle={errorTitle}
        errorMessage={errorMessage}
        onClose={() => {
          hideError();
          closeSupport();
        }}
        closeSupport={closeSupport}
        isSupportOpen={isSupportOpen}
      />

      <TermsAndConditions
        title={t('sellWizard.sfhWizard.review.termsTitle')}
        isOpen={isTermsOpen}
        handleClose={closeTerms}
        handleBack={closeTerms}
        handleNext={acceptTermsFromModal}
        avoidClearingStorage
        overModal
        hideScrollbar
      >
        {termsPdfUrl ? (
          <TermsPdfPages ref={termsPdfContainerRef}>
            <Document
              file={termsPdfUrl}
              onLoadSuccess={({ numPages }) => setTermsNumPages(numPages)}
              loading={
                <Paragraph>
                  {t('sellWizard.sfhWizard.review.loading')}
                </Paragraph>
              }
              error={
                <Paragraph>
                  {t('sellWizard.sfhWizard.review.loading')}
                </Paragraph>
              }
            >
              {Array.from({ length: termsNumPages }, (_, index) => (
                <Page
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  pageNumber={index + 1}
                  width={termsPdfWidth || undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ))}
            </Document>
          </TermsPdfPages>
        ) : (
          <Paragraph>{t('sellWizard.sfhWizard.review.loading')}</Paragraph>
        )}
      </TermsAndConditions>

      <SelectPaymentOptions
        open={isPaymentDrawerOpen}
        setOpen={setIsPaymentDrawerOpen}
        paymentMethods={creditCardPaymentMethods}
        selectedPaymentMethod={activePaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        accountUid={accountUid}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        wizardData={{}}
        setWizardData={() => {}}
        setSuccessToast={() => {}}
        creditCardOnly
        onAddPaymentMethodClick={() =>
          sfhGtmEvent(SFH_ADD_CC_CLICKED, { sku: vaultSymbol, page: 'review' })
        }
      />
      {isAddAddressOpen && (
        <AddDeliveryAddressModal
          isOpen={isAddAddressOpen}
          onClose={() => {
            removeFromSessionStorage(ADD_ADDRESS_WIZARD);
            setIsAddAddressOpen(false);
          }}
          title={t('buyWizard.addYourDeliveryAddress')}
          page="address"
          avoidClearingStorage
          isAddAddressOpen={isAddAddressOpen}
          setIsAddAddressOpen={setIsAddAddressOpen}
        />
      )}
    </SectionLoader>
  );
};

SellFromHomeReview.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  quantity: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({}),
  setSelectedPaymentMethod: PropTypes.func,
  selectedOriginAddress: PropTypes.shape({
    AddressUid: PropTypes.string,
    AddressLine: PropTypes.string,
    AddressLine2: PropTypes.string,
    City: PropTypes.string,
    StateRegion: PropTypes.string,
    PostalCode: PropTypes.string,
    CountryIso3Code: PropTypes.string,
  }),
  initialQuote: PropTypes.shape({
    QuoteUid: PropTypes.string,
    QuoteStatusItem: PropTypes.shape({}),
  }),
  termsAccepted: PropTypes.bool,
  setTermsAccepted: PropTypes.func,
  termsPdfUrl: PropTypes.string,
  isTermsContentLoading: PropTypes.bool,
  handleSubmitOrder: PropTypes.func,
};

export default SellFromHomeReview;
